import {useLazyQuery} from '@apollo/client'
import React, {useEffect, useRef, useState} from 'react'
import {Form, Button, Col, Modal, Row, Alert, Spinner, Accordion} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {GET_ZIP_INFO} from '../../../gql/queries/leadsQuery'
import {IResZipCode} from '../../../types/quoteTypes'
import {toast} from 'react-toastify'
import {GENDER_OPTIONS, INIT_PEOPLE, SIZE_PAGE_QUICK_QUOTE} from '../../../helpers/consts'
import {number} from 'yup'
import {calculateAge, getYearFromEffectiveDate} from '../../../helpers'
import { DateInput } from 'app/tfi/components/DateInput'
import {IForm} from '../../oneAddQuote/oneQuoteTypes'

interface IPerson {
  age: string
  dob: string
  gender: string
  isPregnant: boolean
  pregnantWith: string
  isParent: boolean
  usesTobacco: boolean
  lastTobaccoUseDate: string
  hasMec: boolean
  aptcEligible: boolean
  utilizationLevel: string
  relationship: string
  doesNotCohabitate: string
  currentEnrollment?: null | {
    planId: string
    effectiveDate: string
    usesTobacco: boolean
  }
  id: number
}

interface IModalAddQProps {
  isOpen: boolean
  onClose: () => void
  info?: {
    id: string
    dateBirthLead: string | null | undefined
  }
}

export const ModalAddQuickQuote: React.FC<IModalAddQProps> = ({isOpen, onClose, info}) => {
  const [gqlGetMarket, {data, loading: loadingZipCode, error}] = useLazyQuery<IResZipCode>(
    GET_ZIP_INFO,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const refControl = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const [showMore, setShowMore] = useState(false)

  const nextMonthEffectiveDate = () => {
    const today = new Date()
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
    return nextMonth.toISOString().split('T')[0] // Devuelve en formato 'YYYY-MM-DD'
  }

  const defaultEffectiveDate = nextMonthEffectiveDate()

  const [formData, setFormData] = useState({
    queryType: 'BUSCAR_PLANES',
    effectiveDate: defaultEffectiveDate,
    year: getYearFromEffectiveDate(defaultEffectiveDate),
    householdIncome: '0',
    market: 'Individual',
    countyfips: '', //17033
    countyfipsName: '',
    state: '', //IL
    zipCode: '', //62466
    limit: SIZE_PAGE_QUICK_QUOTE,
    offset: 0,
    order: 'asc',
    utilizationLevel: 'Low',
    numberOfMembers: '1',
    personOne: {
      age: '',
      dob: '',
      gender: 'Male',
      isPregnant: false,
      pregnantWith: '',
      isParent: false,
      usesTobacco: false,
      lastTobaccoUseDate: '',
      hasMec: false,
      aptcEligible: true,
      utilizationLevel: '',
      relationship: '',
      doesNotCohabitate: '',
      currentEnrollment: null,
    },
    people: [], // Add the required 'people' property
  } as IForm)
  const [arrPeople, setArrPeople] = useState<IPerson[]>([])

  // const [eventKey, setEventKey] = useState('0')
  // const [showMore, setShowMore] = useState({personOne: false, people: {}})

  const [infoEnrollement, setInfoEnrollement] = useState({
    id: null as null | number,
    effectiveDate: null,
    planId: null as null | string,
    usesTobacco: false,
  })

  useEffect(() => {
    if (!data) return
    if (data.zipcodeByZip) {
      setFormData((p) => ({
        ...p,
        countyfips: data.zipcodeByZip.countyFips,
        state: data.zipcodeByZip.stateId,
        countyfipsName:
          data.zipcodeByZip.countyNamesAll +
          '(' +
          data.zipcodeByZip.countyFipsAll +
          ')/' +
          data.zipcodeByZip.stateName +
          '-' +
          data.zipcodeByZip.stateId +
          '/' +
          data.zipcodeByZip.zipCode,
      }))
    }
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (info?.dateBirthLead) {
      setFormData((p) => ({...p, dob: info.dateBirthLead || ''}))
    }
  }, [info])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value, type, checked} = e.target
    if (name === 'effectiveDate') {
      setFormData((prevState) => ({
        ...prevState,
        effectiveDate: value,
        year: getYearFromEffectiveDate(value),
      }))
      return
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const obj = {
      ...formData,
      personOne: {
        ...formData.personOne,
        currentEnrollment:
          infoEnrollement.id === 1
            ? {
                id: 1,
                effectiveDate: infoEnrollement.effectiveDate,
                planId: infoEnrollement.planId,
                usesTobacco: infoEnrollement.usesTobacco,
              }
            : null,
      },
      householdIncome: formData.householdIncome.replaceAll(',', ''),
      people: [...arrPeople].map((inf) => {
        if (inf.id === infoEnrollement.id) {
          return {
            ...inf,
            currentEnrollment: {
              id: inf.id,
              effectiveDate: infoEnrollement.effectiveDate,
              planId: infoEnrollement.planId,
              usesTobacco: infoEnrollement.usesTobacco,
            },
          }
        }
        return {...inf}
      }),
    }
    const objStr = JSON.stringify(obj)
    // console.log('obj', obj)
    const decode = window.btoa(objStr)
    navigate(`/quote/${encodeURIComponent(decode)}`)
    onClose()
  }

  const handleAddPeople = () => {
    // setEventKey(`${arrPeople.length + 1}`)
    setArrPeople((p) => [
      ...p,
      {
        id: Date.now(),
        ...INIT_PEOPLE,
      },
    ])
  }

  const handleZipCodeLookup = () => {
    // console.log('blur or enter')

    if (formData.zipCode.length < 3) return

    setFormData((p) => ({
      ...p,
      countyfips: '',
      state: '',
    }))

    gqlGetMarket({
      variables: {
        zipCode: formData.zipCode,
      },
    })
  }

  // console.log('infoEnrollement.usesTobacco', infoEnrollement.usesTobacco)

  return (
    <Modal show={isOpen} onHide={onClose} className='modal-add-quote' dialogClassName='modal-90w'>
      <Modal.Header closeButton>
        <Modal.Title>
          Quick Quote
          {/* {loadingZipCode && <Spinner animation='border' size='sm' className='ms-2' />} */}
        </Modal.Title>
      </Modal.Header>
      {/* {page === 'form' ? ( */}
      <Form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault()
        }}
      >
        <Modal.Body>
          <Alert variant='primary' className='my-5 d-flex align-items-center gap-2'>
            <span>
              <i className='bi bi-exclamation-circle fs-1' style={{color: 'inherit'}} />
            </span>
            Please fill in the necessary information below to receive a quote for insurance plans
          </Alert>
          <Row className='d-flex align-items-center justify-content-between'>
            <Col lg={2} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Household Income</Form.Label>
                <Form.Control
                  type='text'
                  name='householdIncome'
                  value={formData.householdIncome}
                  onChange={(e) => {
                    const {value} = e.target
                    const noComma = value.replaceAll(',', '')
                    if (noComma !== '' && isNaN(Number(noComma))) return
                    const str = Intl.NumberFormat('en-US').format(Number(noComma))
                    setFormData((p) => ({...p, householdIncome: str}))
                  }}
                  required
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={2} sm={6} xs={6}>
              <Form.Group>
                <Form.Label title='People in their tax household'>Number of Members</Form.Label>
                <Form.Control
                  type='number'
                  name='numberOfMembers'
                  value={formData.numberOfMembers}
                  required
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      numberOfMembers: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Zip Code</Form.Label>
                <Form.Control
                  ref={refControl}
                  type='text'
                  name='zipCode'
                  value={formData.zipCode}
                  onChange={handleChange}
                  onBlur={handleZipCodeLookup}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // Evitar submit
                      handleZipCodeLookup()
                    }
                  }}
                />
              </Form.Group>
            </Col>

            <Col lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>County FIPS</Form.Label>{' '}
                {loadingZipCode && (
                  <span>
                    <Spinner animation='border' size='sm' />
                  </span>
                )}
                <Form.Select
                  disabled={loadingZipCode || formData.zipCode?.length < 3}
                  value={formData.countyfips}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      countyfips: e.target.value,
                      countyfipsName: e.target.selectedOptions[0].text,
                    }))
                  }
                >
                  <option value=''>Select county FIPS</option>
                  {data &&
                    data?.zipcodeByZip?.countyNamesAll?.split('|').map((str, idx) => (
                      <option key={str} value={data?.zipcodeByZip?.countyFipsAll?.split('|')[idx]}>
                        {str} - {data?.zipcodeByZip?.countyFipsAll?.split('|')[idx]}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Effective date</Form.Label>
                <div className='d-flex gap-2 align-items-center'>
                  <DateInput
                    id='effectiveDate'
                    name='effectiveDate'
                    value={formData.effectiveDate ?? ''}
                    onChange={(_, value) =>
                      setFormData((p) => ({
                        ...p,
                        effectiveDate: value,
                        year: getYearFromEffectiveDate(value),
                      }))
                    }
                  />
                  {(() => {
                    const effectiveMonth = formData.effectiveDate
                      ? new Date(formData.effectiveDate).getMonth() + 1
                      : null
                    if (effectiveMonth === 11 || effectiveMonth === 12) {
                      const currentYear = new Date().getFullYear()
                      const effectiveYear = formData.effectiveDate
                        ? new Date(formData.effectiveDate).getFullYear()
                        : currentYear
                      const nextYear = effectiveYear + 1
                      return (
                        <Button
                          size='sm'
                          variant='info'
                          type='button'
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              effectiveDate: `${nextYear}-01-01`,
                              year: nextYear,
                            }))
                          }
                        >
                          Plans {nextYear}
                        </Button>
                      )
                    }
                    return null
                  })()}
                </div>
              </Form.Group>
            </Col>

            <Col style={{display: 'none'}} lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>County FIPS</Form.Label>
                <Form.Control
                  type='text'
                  name='countyfips'
                  value={formData.countyfips}
                  required
                  // onChange={handleChange}
                  readOnly
                />
              </Form.Group>
            </Col>
            <Col style={{display: 'none'}} lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>State</Form.Label>
                <Form.Control
                  type='text'
                  name='state'
                  value={formData.state}
                  required
                  // onChange={handleChange}
                  readOnly
                />
              </Form.Group>
            </Col>
            {/* <Col lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Order</Form.Label>
                <Form.Control
                  as='select'
                  name='order'
                  value={formData.order}
                  onChange={handleChange}
                >
                  <option value='asc'>Ascending</option>
                  <option value='desc'>Descending</option>
                </Form.Control>
              </Form.Group>
            </Col> */}
          </Row>
          <hr />
          <div>
            {/* Tabla para mostrar los miembros */}
            <div className='mt-4 table-scrollable table-container'>
              <div className='d-flex justify-content-between align-items-center'>
                <h5 className='mb-3'>Members</h5>
                <Button variant='link' size='sm' onClick={() => setShowMore((prev) => !prev)}>
                  {showMore ? 'Show less' : 'Show more'}
                </Button>
              </div>
              <table className='w-100 table-scrollable table-scroll table-light table-striped table-sm'>
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'>#</th>
                    <th className='fw-bold'>Date of Birth</th>
                    <th className='fw-bold'>Age</th>
                    <th className='fw-bold'>Gender</th>
                    <th className='fw-bold'>Uses Tobacco</th>
                    <th className='fw-bold'>Is Pregnant</th>
                    <th className='fw-bold show-more'>APTC Eligible</th>
                    {showMore && (
                      <>
                        <th className='fw-bold show-more'>Is Parent</th>

                        <th className='fw-bold show-more'>Has MEC</th>
                      </>
                    )}
                    {/* <th className='fw-bold'>Has Married Couple</th> */}
                    <th className='fw-bold'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Mostrar el primer miembro (personOne) */}
                  <tr>
                    <td>1</td>
                    <td>
                      <DateInput
                        id='dobPersonOne'
                        name='dob'
                        value={formData.personOne?.dob || ''}
                        onChange={(_, value) => {
                          if (!value) {
                            setFormData((p) => ({
                              ...p,
                              personOne: {...p.personOne, dob: '', age: ''},
                            }))
                            return
                          }
                          const age = calculateAge(value)
                          setFormData((p) => ({
                            ...p,
                            personOne: {
                              ...p.personOne,
                              dob: value,
                              age: age ? `${age}` : '',
                              hasMec: age && Number(age) < 19 ? true : false,
                            },
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        className='input-age'
                        required={Boolean(!formData.personOne?.age && !formData.personOne?.dob)}
                        type='number'
                        name='Age'
                        disabled={Boolean(formData.personOne?.dob)}
                        value={formData.personOne?.age}
                        onChange={(e) => {
                          const { value } = e.target
                          if (value === '') {
                            setFormData((p) => ({
                              ...p,
                              personOne: { ...p.personOne, age: '' },
                            }))
                            return
                          }
                          if (isNaN(Number(value))) return

                          setFormData((p) => ({
                            ...p,
                            personOne: {
                              ...p.personOne,
                              age: value,
                              hasMec: Number(value) < 19 ? true : false, // 👈 fuerza MEC si la edad < 19
                            },
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        required
                        className='input-gender'
                        as='select'
                        name='gender'
                        value={formData.personOne?.gender}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, gender: e.target.value},
                          }))
                        }}
                      >
                        {GENDER_OPTIONS.map(({value, label}) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td>
                      <Form.Check
                        type='switch'
                        name='usesTobacco'
                        id='usesTobacco_1'
                        checked={formData.personOne?.usesTobacco}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, usesTobacco: e.target.checked},
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type='switch'
                        name='isPregnant'
                        id='isPregnant'
                        checked={formData.personOne?.isPregnant}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, isPregnant: e.target.checked},
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type='switch'
                        name='aptcEligible'
                        id='aptcEligible'
                        checked={formData.personOne?.aptcEligible}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, aptcEligible: e.target.checked},
                          }))
                        }}
                      />
                    </td>
                    {showMore && (
                      <>
                        <td className='show-more'>
                          <Form.Check
                            type='switch'
                            name='isParent'
                            id='isParent'
                            checked={formData.personOne?.isParent}
                            onChange={(e) => {
                              setFormData((p) => ({
                                ...p,
                                personOne: {...p.personOne, isParent: e.target.checked},
                              }))
                            }}
                          />
                        </td>

                        <td className='show-more'>
                          <Form.Check
                            type='switch'
                            name='hasMec'
                            id='hasMec'
                            checked={formData.personOne?.hasMec}
                            onChange={(e) => {
                              setFormData((p) => ({
                                ...p,
                                personOne: {...p.personOne, hasMec: e.target.checked},
                              }))
                            }}
                          />
                        </td>
                      </>
                    )}
                    <td> </td>
                  </tr>
                  {/* Mostrar los demás miembros (arrPeople) */}
                  {arrPeople?.map((inf, idx) => (
                    <tr key={inf.id}>
                      <td>{idx + 2}</td>
                      <td>
                        <DateInput
                          id={`dob_${inf.id}`}
                          name='dob'
                          value={inf.dob || ''}
                          onChange={(_, value) => {
                            const ageC = calculateAge(value)
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id
                                  ? {
                                      ...person,
                                      dob: value,
                                      age: ageC ? `${ageC}` : '',
                                      hasMec: ageC && ageC < 19 ? true : false,
                                    }
                                  : person
                              )
                            )
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type='number'
                          name='Age'
                          className='input-age'
                          required={Boolean(!inf.age && !inf.dob)}
                          disabled={Boolean(inf.dob)}
                          value={inf.age}
                          onChange={(e) => {
                            const {value} = e.target

                            if (value === '') {
                              setArrPeople((prevPeople) =>
                                prevPeople.map((person) =>
                                  person.id === inf.id ? {...person, age: ''} : person
                                )
                              )
                            }
                            if (isNaN(Number(value))) return

                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id
                                  ? {
                                      ...person,
                                      age: value,
                                      hasMec: Number(value) < 19 ? true : false,
                                    }
                                  : person
                              )
                            )
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          required
                          as='select'
                          name='gender'
                          value={inf.gender}
                          onChange={(e) => {
                            const {value} = e.target
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id ? {...person, gender: value} : person
                              )
                            )
                          }}
                        >
                          {GENDER_OPTIONS.map(({value, label}) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </Form.Control>
                      </td>
                      <td>
                        <Form.Group>
                          <Form.Check
                            type='switch'
                            name='usesTobacco'
                            id={`usesTobacco_${inf.id}`}
                            checked={inf.usesTobacco}
                            onChange={(e) => {
                              const {checked} = e.target
                              setArrPeople((prevPeople) =>
                                prevPeople.map((person) =>
                                  person.id === inf.id ? {...person, usesTobacco: checked} : person
                                )
                              )
                            }}
                          />
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Check
                          type='switch'
                          name='isPregnant'
                          id={`isPregnant_${inf.id}`}
                          checked={inf.isPregnant}
                          onChange={(e) => {
                            const {checked} = e.target
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id ? {...person, isPregnant: checked} : person
                              )
                            )
                          }}
                        />
                      </td>
                      <td>
                        <Form.Check
                          type='switch'
                          name='aptcEligible'
                          id={`aptcEligible_${inf.id}`}
                          checked={inf.aptcEligible}
                          onChange={(e) => {
                            const {checked} = e.target
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id ? {...person, aptcEligible: checked} : person
                              )
                            )
                          }}
                        />
                      </td>
                      {showMore && (
                        <>
                          <td className='show-more'>
                            <Form.Check
                              type='switch'
                              name='isParent'
                              id={`isParent_${inf.id}`}
                              checked={inf.isParent}
                              onChange={(e) => {
                                const {checked} = e.target
                                setArrPeople((prevPeople) =>
                                  prevPeople.map((person) =>
                                    person.id === inf.id ? {...person, isParent: checked} : person
                                  )
                                )
                              }}
                            />
                          </td>

                          <td className='show-more'>
                            <Form.Check
                              type='switch'
                              name='hasMec'
                              id={`hasMec${inf.id}`}
                              checked={inf.hasMec}
                              onChange={(e) => {
                                const {checked} = e.target
                                setArrPeople((prevPeople) =>
                                  prevPeople.map((person) =>
                                    person.id === inf.id ? {...person, hasMec: checked} : person
                                  )
                                )
                              }}
                            />
                          </td>
                        </>
                      )}
                      <td>
                        <Button
                          variant='danger'
                          size='sm'
                          className='ms-2'
                          onClick={() => {
                            setArrPeople((prev) => prev.filter((person) => person.id !== inf.id))
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className='mt-2'>
            <Button size='sm' variant='info' type='button' onClick={handleAddPeople}>
              Add Member
            </Button>
          </div>

          <Row className='mt-4'>
            {/* <Col lg={4}>
                <Form.Group>
                  <Form.Label>Market</Form.Label>
                  <Form.Control
                    as='select'
                    name='market'
                    value={formData.market}
                    onChange={handleChange}
                  >
                    <option value='Individual'>Individual</option>
                  </Form.Control>
                </Form.Group>
              </Col> */}
          </Row>

          {/* <Row className='mt-4'>
              <Col lg={3}>
                <Form.Group>
                  <Form.Label>Limit</Form.Label>
                  <Form.Control
                    type='number'
                    name='limit'
                    value={formData.limit}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group>
                  <Form.Label>Offset</Form.Label>
                  <Form.Control
                    type='number'
                    name='offset'
                    value={formData.offset}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row> */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' type='button' onClick={onClose}>
            Close
          </Button>
          <Button
            variant='primary'
            type='submit'
            disabled={
              !(
                (
                  formData.countyfips &&
                  formData.zipCode && // Validar que zipCode no esté vacío
                  Number(formData.householdIncome.replaceAll(',', '')) > 0 && // Validar que householdIncome sea mayor a 0
                  (formData.personOne.age || formData.personOne.dob)
                ) // Validar que exista age o dob en personOne
              )
            }
          >
            Search
          </Button>
        </Modal.Footer>
      </Form>
      {/* ) : (
        <>
          <Modal.Body style={{backgroundColor: '#f8f8f8'}}>
            <div style={{minHeight: '600px'}}>
              <div className='mb-4'>
                <button
                  className='d-flex align-items-center gap-2 bg-light-primary rounded'
                  style={{border: 0}}
                  onClick={() => {
                    setPage('form')
                    setResult(null)
                  }}
                >
                  <i className='bi bi-arrow-left' style={{color: 'inherit'}} />
                  Volver
                </button>
              </div>
              {result && <ResultsAddQuote result={result} />}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={onClose}>Acept</Button>
          </Modal.Footer>
        </>
      )} */}
    </Modal>
  )
}
